/**
 * Name casing for display.
 *
 * The names in this system came out of spreadsheets, and they are spelled the
 * way whoever typed them had their caps lock: `AbhilaSH lakkepuram`,
 * `KOTESWARI`, `moulish kumar`. The screen should read them back properly
 * without anybody re-typing 2,291 rows.
 *
 * Applied on the way to the screen and never on the way to the database. The
 * stored spelling is what the person wrote, and it is also what the importer
 * matched on -- `normalise_key` strips case and punctuation to decide that
 * `Tata 1MG` and `Tata-1mg` are one client, so a formatter that wrote back
 * would be rewriting a dedup key to make a column look tidy. Edit forms bind
 * to the raw value for the same reason: prefilling a formatted name would turn
 * a display choice into a silent edit the next time somebody pressed Save.
 *
 * Deliberately for PEOPLE only. Client and company names are not run through
 * this and must not be: `Tata 1MG` becomes `Tata 1mg`, `HDFC` becomes `Hdfc`,
 * and both are wrong in a way a person's name never is.
 */

/** `Mc` is a patronymic prefix in every name that carries it. */
const MC = /^mc(.+)$/;

/**
 * `Mac` is not. `Machado`, `Mackay` and `Macey` are ordinary words that happen
 * to start with the same three letters, so this is an allowlist rather than a
 * rule -- a name not on it is cased normally, which is the failure that looks
 * like nothing rather than the one that invents a capital.
 */
const MAC_NAMES = new Set([
  'macdonald', 'macgregor', 'macleod', 'macintosh', 'macintyre', 'mackenzie',
  'macmillan', 'macarthur', 'macneil', 'macpherson',
]);

/**
 * Generational suffixes and regnal numbers, which are not words.
 *
 * Only ever applied to a token that is not the first one. `vi` is both a
 * numeral and a given name, and `Vi Nguyen` must not be shouted at to make
 * `John Smith III` come out right.
 */
const NUMERALS = /^(?:i{1,3}|iv|v|vi{1,3}|ix|x)$/;

/** Capitalise one run of letters, leaving anything else as it is. */
function capitalise(word: string): string {
  if (!word) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * One space-delimited token: the level where Mc, initials and numerals live.
 *
 * Hyphens and apostrophes are handled by splitting on them and recursing, so
 * `anne-marie` and `d'souza` need no rules of their own beyond deciding which
 * side of the mark gets a capital.
 */
function caseToken(token: string, first = false): string {
  if (!token) return token;

  // Nothing with a letter to case -- punctuation, digits, or a script that
  // has no upper and lower case at all. Returned untouched rather than
  // guessed at.
  if (!/[a-z]/i.test(token)) return token;

  // Hyphenated: each side is a name in its own right. `Anne-Marie`.
  if (token.includes('-')) {
    return token.split('-').map((part) => caseToken(part, first)).join('-');
  }

  const lower = token.toLowerCase();

  // Initials, written with stops: `a.b.` -> `A.B.`, and `k.` -> `K.`
  // Only when every piece is a single letter, so `jr.` is left to the rules
  // below and a name with a stray full stop is not shouted at.
  if (token.includes('.')) {
    const pieces = lower.split('.');
    if (pieces.every((p) => p.length <= 1)) return token.toUpperCase();
  }

  // `II`, `III`, `IV` -- a numeral, not a word, so it is not `Iii`. Never the
  // first token: see NUMERALS.
  if (!first && NUMERALS.test(lower)) return lower.toUpperCase();

  // `O'Brien`, `D'Souza`. The capital goes after the apostrophe only when a
  // single letter precedes it: that is what a patronymic looks like, and it
  // is what keeps a possessive from becoming `Shaan'S`.
  const apostrophe = lower.indexOf("'");
  if (apostrophe === 1) {
    return (
      token.charAt(0).toUpperCase()
      + "'"
      + caseToken(token.slice(apostrophe + 1))
    );
  }

  // `McKenzie`. Two capitals, and the second one is the point.
  const mc = MC.exec(lower);
  if (mc && mc[1].length >= 2) return `Mc${capitalise(mc[1])}`;

  if (MAC_NAMES.has(lower)) return `Mac${capitalise(lower.slice(3))}`;

  // A bare run of capitals two letters or shorter is left alone: it is far
  // more likely to be initials somebody typed without stops (`KV Rao`, `MS
  // Dhoni`) than a name that wants casing.
  if (token === token.toUpperCase() && token.length <= 2) return token;

  return capitalise(token);
}

/**
 * A person's name, cased for display. `AbhilaSH lakkepuram` reads back as
 * `Abhilash Lakkepuram`.
 *
 * Returns null for a null or empty value, matching `formatPhone`, so call
 * sites keep their own blank rendering -- some use an em dash, some omit the
 * row entirely.
 *
 * Every token is normalised rather than only the obviously-wrong ones, which
 * is the one real cost here: a surname deliberately capitalised in the middle
 * and not matching `Mc`, `Mac`, `O'` or `D'` -- `DeSouza` written that way on
 * purpose -- comes back as `Desouza`. That is accepted knowingly. The
 * alternative is leaving `AbhilaSH` on screen, because nothing in the string
 * distinguishes a deliberate capital from a slipped one.
 */
export function titleCaseName(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Collapse the runs of spaces a spreadsheet leaves behind, so the tokens
  // below are words rather than empty strings.
  return trimmed
    .split(/\s+/)
    .map((token, i) => caseToken(token, i === 0))
    .join(' ');
}

/**
 * The same, for a call site that has its own fallback and does not want null.
 *
 * `titleCaseName(x) ?? '—'` is the common shape; this exists for the places
 * that already hold a non-null string and would otherwise need the `?? ''`
 * that makes the type check pass and says nothing.
 */
export function titleCaseNameOr(
  value: string | null | undefined,
  fallback: string,
): string {
  return titleCaseName(value) ?? fallback;
}
